import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import getAPIUrl from "../components/API_FIX";

// Get Backend URL from environment variable
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Build API base via helper (null-safe if the env var is missing at module load)
const API = getAPIUrl(BACKEND_URL || '');
const AUTH_API = `${API}/auth`;

// Create Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Set default auth header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

          // Fetch user data
          const response = await axios.get(`${AUTH_API}/me`);
          setUser({
            ...response.data,
            token
          });
        } catch (error) {
          console.error("Auth error:", error);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = async (email, password) => {
    try {
      console.log("Attempting login for:", email);

      // Create form data for token request
      const formData = new FormData();
      formData.append('username', email);  // Note: API expects email in the username field
      formData.append('password', password);

      // Get token
      const response = await axios.post(`${AUTH_API}/token`, formData);
      console.log("Login successful, token received");

      // Store token in localStorage
      localStorage.setItem('token', response.data.access_token);

      // Set default auth header
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;

      // Save user data
      setUser({
        id: response.data.user_id,
        role: response.data.role,
        token: response.data.access_token
      });

      console.log("User authenticated with role:", response.data.role);
      return true;
    } catch (error) {
      console.error("Login error:", error);
      if (error.response && error.response.data) {
        console.error("Login error details:", error.response.data);
      }
      return false;
    }
  };

  // Exchange a Google ID token for an app token (same session model as login()).
  const loginWithGoogle = async (credential) => {
    try {
      const response = await axios.post(`${AUTH_API}/google`, { credential });
      localStorage.setItem('token', response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      setUser({
        id: response.data.user_id,
        role: response.data.role,
        token: response.data.access_token
      });
      return true;
    } catch (error) {
      console.error("Google login error:", error?.response?.data || error);
      return false;
    }
  };

  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');

    // Remove auth header
    delete axios.defaults.headers.common['Authorization'];

    // Clear user data
    setUser(null);
  };

  const register = async (username, email, password) => {
    try {
      console.log("Registering user:", email);
      const response = await axios.post(`${AUTH_API}/register`, {
        username,
        email,
        password,
        role: "reporter"  // Default role for new users
      });

      console.log("Registration successful, response:", response.data);

      // Auto login after registration
      return await login(email, password);
    } catch (error) {
      console.error("Registration error:", error);
      if (error.response && error.response.data) {
        console.error("Registration error details:", error.response.data);
      }
      return false;
    }
  };

  const isAdmin = () => {
    return user && user.role === "admin";
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, logout, register, isAdmin, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};
